import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Box, Typography, Tooltip} from '@mui/material';
import axios from 'axios';
import ApiUtils from '../../../utils/apiUtils';
import base_url from '../../../baseUrls';
import {COLORS} from '../../../styles/styleConstants';
import {inspectionApiTypes} from '../../jobs/ipermitJobPages/listViews/inspections/inspectionsGridOptions';
import {
  connectUserSocket,
  disconnectUserSocket,
  getBrowserSessionId,
  SOCKET_EVENTS,
} from '../../../sockets/userSocket';

/**
 * A backstop, not the main path: the server pushes {@link AI_DASHBOARD_CHANGED} as counts move.
 * The poll still catches what no write announces - a call turning stale after 30 minutes is only
 * the clock moving - and anything sent while the socket was down.
 */
const REFRESH_INTERVAL_SECONDS = 60;

/** `data-ready` type the server sends when a count may have moved (`dashboardEvents.ts`). */
const AI_DASHBOARD_CHANGED = 'ai-dashboard-changed';

/**
 * The cards, in reading order. `accent` is the left border, keyed to the same palette the grid's
 * status chips use so a card and its rows look related.
 *
 * `target` is where a click lands: a dashboard tab, optionally with the inspections view to
 * select. Cards whose number has no matching view are left without one rather than jumping
 * somewhere that shows a different set of rows than the card counted.
 */
const CARD_DEFINITIONS = [
  {
    key: 'activeAgents',
    label: 'Active Agents',
    accent: '#39DE5D',
    hint: 'Agents switched on for AI calls, out of everyone cleared to take them.',
    render: (d) => `${d.activeAgents ?? 0} / ${d.totalAgents ?? 0}`,
    target: {tab: 'agents'},
  },
  {
    key: 'activeCalls',
    label: 'Calls In Progress',
    accent: '#F4F402',
    hint: 'Calls the dialer has started and not yet reported as finished.',
    render: (d) => d.activeCalls ?? 0,
    warn: (d) => (d.staleCalls > 0
      ? `${d.staleCalls} started call${d.staleCalls === 1 ? '' : 's'} never reported finishing`
      : ''),
    target: {tab: 'allCalls', fallbackTab: 'myCalls'},
  },
  {
    key: 'attendedToday',
    label: 'Attended Today',
    accent: '#55CB8B',
    hint: 'Calls that recorded an outcome today, answered or not.',
    render: (d) => d.attendedToday ?? 0,
    target: {tab: 'allCalls', fallbackTab: 'myCalls'},
  },
  {
    key: 'dueToday',
    label: 'Due Today',
    accent: '#7FB3F5',
    hint: 'Inspections whose AI call is scheduled for today and still open.',
    render: (d) => d.dueToday ?? 0,
  },
  {
    key: 'rescheduled',
    label: 'Rescheduled',
    accent: '#7FB3F5',
    hint: 'Inspections waiting on a callback that has been pushed to a later date.',
    render: (d) => d.rescheduled ?? 0,
    target: {tab: 'allInspections', view: inspectionApiTypes.aiRescheduled},
  },
  {
    key: 'needsVerify',
    label: 'Needs Verify',
    accent: '#F32929',
    hint: 'Inspections whose AI call ended in an outcome a person has to review.',
    render: (d) => d.needsVerify ?? 0,
    target: {tab: 'needsAttention'},
  },
];

const SummaryCard = ({definition, data, loading, onNavigate}) => {
  const warning = definition.warn ? definition.warn(data) : '';
  const clickable = !!definition.target && !!onNavigate;

  const activate = () => {
    if (clickable) onNavigate(definition.target);
  };

  const hint = clickable ? `${definition.hint} Click to open.` : definition.hint;

  return (
    <Tooltip title={warning || hint} placement={'top'} arrow>
      <Box
        onClick={activate}
        onKeyDown={(event) => {
          if (!clickable) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            activate();
          }
        }}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        aria-label={clickable ? `${definition.label}, open` : undefined}
        sx={{
          flex: '1 1 0',
          minWidth: '180px',
          cursor: clickable ? 'pointer' : 'default',
          transition: 'box-shadow 120ms, border-color 120ms',
          ...(clickable && {
            '&:hover': {
              boxShadow: '0 1px 6px rgba(0, 0, 0, 0.18)',
            },
            '&:focus-visible': {
              outline: `2px solid ${COLORS.globalBlue}`,
              outlineOffset: '2px',
            },
          }),
          borderLeft: `4px solid ${definition.accent}`,
          borderTop: `1px solid ${COLORS.backgroundMain}`,
          borderRight: `1px solid ${COLORS.backgroundMain}`,
          borderBottom: `1px solid ${COLORS.backgroundMain}`,
          borderRadius: '6px',
          px: 1.5,
          py: 0.5,
          backgroundColor: '#fff',
          // Label and number on one line: the cards sit above every tab, so their height comes
          // out of each grid's.
          alignItems: 'baseline',
          display: 'flex',
          gap: 1,
          justifyContent: 'space-between',
        }}
      >
        <Typography
          sx={{
            fontSize: '0.7rem',
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: COLORS.fontPale,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {definition.label}
        </Typography>
        <Box sx={{alignItems: 'baseline', display: 'flex', flexShrink: 0, gap: 0.75}}>
          <Typography sx={{fontSize: '1.15rem', fontWeight: 700, lineHeight: 1.3}}>
            {loading ? '—' : definition.render(data)}
          </Typography>
          {!loading && warning && (
            <Typography sx={{color: COLORS.globalRed, fontSize: '0.7rem', fontWeight: 600}}>
              !
            </Typography>
          )}
        </Box>
      </Box>
    </Tooltip>
  );
};

SummaryCard.propTypes = {
  definition: PropTypes.instanceOf(Object).isRequired,
  data: PropTypes.instanceOf(Object).isRequired,
  loading: PropTypes.bool,
  onNavigate: PropTypes.func,
};

/**
 * Count cards above the AI dashboard grids. Self-contained: it fetches its own summary and
 * refreshes on the same cadence as the recent-calls board, so no parent has to feed it.
 */
export const AiSummaryCards = ({refreshTimestamp, onNavigate}) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const isMountedRef = useRef(false);
  const isFetchingRef = useRef(false);
  /** A push that landed mid-fetch; that fetch may predate the change, so one more follows it. */
  const refetchPendingRef = useRef(false);

  const fetchSummary = useCallback(async ({showLoader = false} = {}) => {
    if (isFetchingRef.current) {
      refetchPendingRef.current = true;
      return;
    }
    isFetchingRef.current = true;
    if (showLoader && isMountedRef.current) setLoading(true);

    try {
      const authToken = await ApiUtils.getAccessToken();
      const response = await axios.get(`${base_url.api}ai/dashboard-summary`, {
        headers: {Authorization: authToken},
      });
      const payload = response?.data?.data ?? response?.data ?? {};
      if (isMountedRef.current) {
        setData(payload);
        setError('');
      }
    } catch (e) {
      if (isMountedRef.current) setError('Unable to load AI summary.');
    } finally {
      if (isMountedRef.current) setLoading(false);
      isFetchingRef.current = false;
      if (refetchPendingRef.current && isMountedRef.current) {
        refetchPendingRef.current = false;
        fetchSummary();
      }
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    fetchSummary({showLoader: true});

    const intervalId = setInterval(() => fetchSummary(), REFRESH_INTERVAL_SECONDS * 1000);
    return () => {
      isMountedRef.current = false;
      clearInterval(intervalId);
    };
  }, [fetchSummary]);

  // Lets a grid refresh pull fresh numbers too, so the cards never disagree with the rows.
  useEffect(() => {
    if (refreshTimestamp) fetchSummary();
  }, [refreshTimestamp, fetchSummary]);

  /**
   * Live updates. Shares the tab's socket through the registry - an agent's tab already has one
   * open for incoming calls - and opens it otherwise, since a supervisor who takes no calls has
   * none. The server only puts supervisors in the room these events go to.
   *
   * Refetches on reconnect as well: anything sent while the socket was down is gone. That listens
   * on the manager (`socket.io`), not for `connect` on the socket, because useUserSocket's cleanup
   * removes every `connect` listener on the shared socket, not only its own.
   */
  useEffect(() => {
    const browserSessionId = getBrowserSessionId();
    let socket = null;
    let cancelled = false;

    const onDataReady = (data) => {
      if (data && typeof data === 'object' && data.type === AI_DASHBOARD_CHANGED) {
        fetchSummary();
      }
    };
    const onReconnect = () => fetchSummary();

    connectUserSocket({browserSessionId}).then((s) => {
      if (!s) return;
      if (cancelled) {
        // Unmounted while connecting: hand back the reference connectUserSocket counted.
        disconnectUserSocket(browserSessionId);
        return;
      }
      socket = s;
      s.on(SOCKET_EVENTS.DATA_READY, onDataReady);
      s.io.on('reconnect', onReconnect);
    });

    return () => {
      cancelled = true;
      if (socket) {
        socket.off(SOCKET_EVENTS.DATA_READY, onDataReady);
        socket.io.off('reconnect', onReconnect);
        disconnectUserSocket(browserSessionId);
      }
    };
  }, [fetchSummary]);

  if (error) {
    return (
      <Box sx={{px: 2, py: 1}}>
        <Typography color={'error'} sx={{fontSize: '0.8rem'}}>{error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        px: 2,
        pt: '5px',
        pb: 0.25,
        width: '100%',
      }}
    >
      {CARD_DEFINITIONS.map((definition) => (
        <SummaryCard
          key={definition.key}
          definition={definition}
          data={data}
          loading={loading}
          onNavigate={onNavigate}
        />
      ))}
    </Box>
  );
};

AiSummaryCards.propTypes = {
  /** Bump this to refetch, e.g. when a grid below is refreshed. */
  refreshTimestamp: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Called with a card's `target` ({tab, fallbackTab, view}) when it is clicked. */
  onNavigate: PropTypes.func,
};

export default AiSummaryCards;
